import React from 'react';
import { Link, useParams } from 'react-router-dom';

type CaseStudySection = {
  title: string;
  body: string;
};

type CaseStudyData = {
  slug: string;
  title: string;
  subtitle: string;
  accentBgClass: string;
  heroCardBgClass: string;
  highlights: string[];
  sections: CaseStudySection[];
};

const CASE_STUDIES: Record<string, CaseStudyData> = {
  'ai-flashcards': {
    slug: 'ai-flashcards',
    title: 'AI Flashcards',
    subtitle:
      'Self study app that allows students to utilise AI to memorise, learn and master any subject by creating study tools with AI and consulting an AI tutor.',
    accentBgClass: 'bg-[#D8F2FF]',
    heroCardBgClass: 'bg-white/80',
    highlights: ['Role: Product + Frontend', 'Goal: Faster learning loops', 'Focus: Generative AI Study Tools'],
    sections: [
      {
        title: 'Overview',
        body: 'AI Flashcards is an AI study companion that allows students to master any subject by creating study tools like flashcards with AI and asking an AI Tutor for help.',
      },
      {
        title: 'Problem / Opportunity',
        body: 'Students often spend a large amount of time creating study materials before they can actually begin studying. While flashcards are a powerful learning tool, manually creating them from notes can be tedious and time-consuming. I wanted to build a tool that could help students transform their study notes into flashcards automatically using AI, allowing them to focus more on learning rather than preparation. Students also spend a lot of time asking AI chatbots like ChatGPT to just give them answers rather than using these platforms for learning. This is why I developed the AI tutor chat that allows students to chat to the AI tutor who will explain concepts, quiz the student on that concept and give them feedback. The tutor feature can also help create study tools like diagrams and infographics for the student if they feel it’s needed.',
      },
      {
        title: 'My Role',
        body: 'I was the sole developer for this project. I was responsible for the entire development process, from the initial concept to the final product. I was also responsible for the design of the website and the UI/UX of the application.',
      },
      {
        title: 'Challenges',
        body: 'Preventing prompt injection attacks and ensuring the security of the application.',
      },
      {
        title: 'Process',
        body: 'I started by wireframing the application. I then designed the UI/UX of the application using Figma. Then I started planning the architecture of the application. I then started the development of the application. I used the MERN stack to build the application. I used React for the frontend and Node.js for the backend. I used MongoDB for the database. I used OpenAI API for the Generative AI features.',
      },
      {
        title: 'Results',
        body: 'The project was a success. I was able to build a tool that helps students master any subject by creating study tools like flashcards with AI and asking an AI Tutor for help. I was also able to learn how to use AI to generate flashcards and which LLMs are best for different tasks.',
      },
      {
        title: 'Technologies',
        body: 'React, Node.js, MongoDB, OpenAI API, Tailwind CSS, N8N',
      },
    ],
  },
  'glow-up': {
    slug: 'glow-up',
    title: 'Glow Up',
    subtitle:
      'Habit tracker app that allows users to record and track daily habits of their choice, track sleep and visualise their progress.',
    accentBgClass: 'bg-[#FFD5EA]',
    heroCardBgClass: 'bg-white/80',
    highlights: ['Role: Product + Frontend', 'Goal: Build consistent habits', 'Focus: Data visualisation'],
    sections: [
      {
        title: 'Overview',
        body: 'Glow up is an habit tracker app that allows users to record and track daily habits of their choice, track sleep and visualise their progress.',
      },
      {
        title: 'Problem / Opportunity',
        body: 'Many people, myself included, have a hard time building consistent habits. I often find myself struggling to stick to my habits. I wanted to build a tool that could help people build consistent habits and be able to see their progress and visualise their data.',
      },
      {
        title: 'My Role',
        body: 'I was the sole developer for this project. I was responsible for the entire development process, from the initial concept to the final product. I was also responsible for the design of the website and the UI/UX of the application.',
      },
      {
        title: 'Process',
        body: 'I started by wireframing the application. I then designed the UI/UX of the application using Figma. Then I started planning the architecture of the application. I then started the development of the application. I used the MERN stack to build the application. I used React for the frontend and Node.js for the backend. I used MongoDB for the database.',
      },
      {
        title: 'Results',
        body: 'The project was a success. I was able to build a tool that helps people build consistent habits and be able to see their progress and visualise their data.',
      },
      {
        title: 'Technologies',
        body: 'MERN Stack, React, Node js, Express, MongoDB, Charts.js, Tailwind CSS',
      },
    ],
  },
  'gift-genie': {
    slug: 'gift-genie',
    title: 'Gift Genie',
    subtitle:
      'Users fill out a simple form describing the recipient, and the app generates thoughtful, relevant gift ideas along with suggestions on where to purchase them.',
    accentBgClass: 'bg-purple-100',
    heroCardBgClass: 'bg-white/80',
    highlights: ['Status: Case study in progress', 'Goal: Reduce gift-planning effort', 'Focus: Personalisation'],
    sections: [
      {
        title: 'Overview',
        body: 'TODO: Add a short overview of the project, what problem it solved, and why it mattered.',
      },
      {
        title: 'Problem / Opportunity',
        body: 'TODO: Describe the user problem and what existing approaches were missing.',
      },
      {
        title: 'My Role',
        body: 'TODO: Explain what you personally owned (e.g., UX, feature design, AI prompt/workflow, UI).',
      },
      {
        title: 'Process',
        body: 'TODO: Summarise your process: discovery, defining requirements, wireframes, implementation, and iteration.',
      },
      {
        title: 'Results',
        body: 'TODO: Add measurable outcomes if you have them (or qualitative results).',
      },
      {
        title: 'Technologies',
        body: 'TODO: List the stack/tools you used (frontend framework, LLM integration, styling, etc.).',
      },
    ],
  },
  closetful: {
    slug: 'closetful',
    title: 'Closetful',
    subtitle:
      'An AI personal stylist that turns photos of the clothes you already own into editorial outfit suggestions — no shopping required.',
    accentBgClass: 'bg-purple-100',
    heroCardBgClass: 'bg-white/80',
    highlights: ['Role: Product + Frontend', 'Goal: Style from what you own', 'Focus: AI outfit curation'],
    sections: [
      {
        title: 'Overview',
        body: 'Closetful is a full stack application that allows users to upload photos of their clothes and generate outfits based on the clothes they already have. Users can specify the occasion, weather, and other preferences to generate the outfit.',
      },
      {
        title: 'Problem / Opportunity',
        body: 'Many people, myself included, have a hard time finding outfits that look good on them. I often find myself wearing the same few outfits over and over again because I don\'t know what to wear or I often forget about the clothes I already have. I wanted to build a tool that could help people find outfits that look good on them by using AI to generate outfits based on the clothes they already have.',
      },
      {
        title: 'My Role',
        body: 'I was the sole developer for this project. I was responsible for the entire development process, from the initial concept to the final product. I was also responsible for the design of the website and the UI/UX of the application.',
      },
      {
        title: 'Challenges',
        body: 'Handling images and ensuring the security of the application. The images are stored in the cloud and are accessed using a URL. I had to ensure that the images are not stored in the database and are not accessed directly by the user. Another challenge was coming up with the image pipeline so that AI was able to analyse each outfit and return the appropriate information that would be stored in the database. The data on each clothing item needed to be relevant and clear so that AI would understand the colour, style, material, etc. of the clothing item so that appropriate outfits could be generated.',
      },
      {
        title: 'Process',
        body: 'I started by wireframing the application. I then designed the UI/UX of the application using Figma. Then I started planning the architecture of the application. I then started the development of the application. I used the MERN stack to build the application. I used React for the frontend and Node.js for the backend. I used MongoDB for the database. I used Gemini API for the AI generation. I used Anthropic API for the AI generation. I used Cloudinary for the image storage. I used Vercel for the deployment. I used Heroku for the deployment.',
      },
      {
        title: 'Results',
        body: 'The project was a success. I was able to build a tool that helps people find outfits that look good on them by using AI to generate outfits based on the clothes they already have. I was also able to learn how to use AI to generate outfits and which LLMs are best for different tasks.',
      },
      {
        title: 'Technologies',
        body: 'MERN Stack, React, Node js, Express, MongoDB, Gemini API, Anthropic API',
      },
    ],
  },
};

const ProjectCaseStudy: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const caseStudy = slug ? CASE_STUDIES[slug] : undefined;

  if (!caseStudy) {
    return (
      <div className="pt-24 min-h-screen bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Case Study Not Found</h1>
          <p className="text-gray-700 mb-8">
            The project you’re looking for doesn’t exist (yet). Head back to the projects list to pick another.
          </p>
          <Link
            to="/projects"
            className="inline-flex items-center px-6 py-3 rounded-xl btn-secondary hover:bg-soft-pink hover:text-white"
          >
            <span className="material-icons mr-2" aria-hidden>
              arrow_back
            </span>
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 md:pt-32 min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="flex items-center gap-3 mb-10">
          <Link
            to="/projects"
            className="inline-flex items-center text-gray-700 hover:text-soft-pink transition-colors"
          >
            <span className="material-icons mr-1" aria-hidden>
              arrow_back
            </span>
            Back to Projects
          </Link>
          <span className="text-gray-300">/</span>
          <span className="text-gray-600">{caseStudy.title}</span>
        </div>

        <header className="mb-14">
          <div className={`${caseStudy.accentBgClass} rounded-3xl p-10`}>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">{caseStudy.title}</h1>
            <p className="text-lg md:text-xl text-gray-700 max-w-3xl leading-relaxed">{caseStudy.subtitle}</p>

            <div className="mt-8 flex flex-wrap gap-3">
              {caseStudy.highlights.map((h) => (
                <span
                  key={h}
                  className="inline-flex items-center px-4 py-2 rounded-full bg-white/60 border border-white/70 text-gray-800 text-sm font-medium"
                >
                  {h}
                </span>
              ))}
            </div>

          </div>
        </header>

        <div className="space-y-10">
          {caseStudy.sections.map((section) => (
            <section
              key={section.title}
              className="bg-white rounded-2xl shadow-softer p-8 border border-gray-100/80"
            >
              <h2 className="text-3xl font-semibold text-gray-900 mb-4">{section.title}</h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{section.body}</p>
            </section>
          ))}

          <section className="bg-white rounded-2xl shadow-softer p-8 border border-gray-100/80">
            <h2 className="text-3xl font-semibold text-gray-900 mb-4">Gallery / Screenshots</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              TODO: Add screenshots, diagrams, or prototypes. (You can swap this placeholder grid for real images.)
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, idx) => (
                <div
                  key={idx}
                  className="h-44 rounded-xl border border-gray-200 bg-gradient-to-br from-sky-blue/40 to-pastel-pink/40 flex items-center justify-center text-gray-400"
                >
                  Placeholder
                </div>
              ))}
            </div>
          </section>
        </div>

        <footer className="pt-14">
          <div className="bg-gradient-to-r from-soft-pink to-soft-lavender rounded-3xl p-8 text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-semibold mb-2">Want to see more projects?</h2>
                <p className="text-white/90">Browse the rest of the case studies and reach out if you want to collaborate.</p>
              </div>
              <Link
                to="/projects"
                className="btn-secondary bg-white/10 border-white/60 hover:bg-white hover:text-soft-pink transition-colors"
              >
                Back to Projects
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ProjectCaseStudy;

